import { type PackageDetail } from '@utazzo/shared';
import { UzAlert, UzButton, UzCombobox } from '@utazzo/ui';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { useSetPackageHeroInclusions } from '../use-packages';

import {
  useCreateHeroInclusion,
  useHeroInclusionsList,
} from '@/features/hero-inclusions/use-hero-inclusions';
import { resolveMedia } from '@/features/website-cms/components/media.api';

interface Selected {
  heroInclusionId: string;
  name: string;
  iconImageId: string | null;
}

const seed = (items: PackageDetail['heroInclusions']): Selected[] =>
  items.map((i) => ({
    heroInclusionId: i.heroInclusionId,
    name: i.name,
    iconImageId: i.iconImageId,
  }));

/** Resolves a media id → preview URL and renders a small square thumbnail. */
const IconThumb = ({ id }: { id: string | null }): React.JSX.Element => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setUrl(null);
      return;
    }
    resolveMedia(id)
      .then((m) => !cancelled && setUrl(m.url))
      .catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!id) return <span className="text-zinc-400">-</span>;
  return url ? (
    <img src={url} alt="" className="h-8 w-8 rounded object-cover" />
  ) : (
    <span className="text-zinc-300">…</span>
  );
};

interface HeroHighlightsEditorProps {
  pkg?: PackageDetail | null;
  draftValue?: string[];
  onDraftChange?: (ids: string[]) => void;
}

export const HeroHighlightsEditor = ({
  pkg,
  draftValue,
  onDraftChange,
}: HeroHighlightsEditorProps): React.JSX.Element => {
  const isControlled = onDraftChange !== undefined;

  const masterQuery = useHeroInclusionsList({
    page: 1,
    pageSize: 200,
    sortBy: 'sortOrder',
    sortDir: 'asc',
    isActive: true,
  });
  // Always call unconditionally; fall back to '' in controlled mode.
  const set = useSetPackageHeroInclusions(pkg?.id ?? '');
  const createMaster = useCreateHeroInclusion();
  const master = useMemo(() => masterQuery.data?.data ?? [], [masterQuery.data]);

  // ── Controlled (create) mode ──────────────────────────────────────────────
  const [draftSelected, setDraftSelected] = useState<Selected[]>(() => {
    if (!isControlled) return [];
    const ids = draftValue ?? [];
    return ids.map((id) => ({ heroInclusionId: id, name: id, iconImageId: null }));
  });

  useEffect(() => {
    if (!isControlled) return;
    const ids = draftValue ?? [];
    setDraftSelected(
      ids.map((id) => {
        const entry = master.find((m) => m.id === id);
        return {
          heroInclusionId: id,
          name: entry?.name ?? id,
          iconImageId: entry?.iconImageId ?? null,
        };
      }),
    );
  }, [isControlled, draftValue, master]);

  // ── Live (edit) mode ──────────────────────────────────────────────────────
  const [liveSelected, setLiveSelected] = useState<Selected[]>(() =>
    pkg ? seed(pkg.heroInclusions) : [],
  );
  useEffect(() => {
    if (isControlled) return;
    if (pkg) setLiveSelected(seed(pkg.heroInclusions));
  }, [isControlled, pkg]);

  const selected = isControlled ? draftSelected : liveSelected;
  const setSelected = isControlled ? setDraftSelected : setLiveSelected;

  const current = useMemo(() => (pkg ? seed(pkg.heroInclusions) : []), [pkg]);
  const dirty =
    !isControlled &&
    (selected.length !== current.length ||
      selected.some((s, i) => s.heroInclusionId !== current[i]?.heroInclusionId));

  const options = useMemo(
    () =>
      master
        .filter((m) => !selected.some((s) => s.heroInclusionId === m.id))
        .map((m) => ({ value: m.id, label: m.name })),
    [master, selected],
  );

  const add = (id: string): void => {
    const entry = master.find((m) => m.id === id);
    if (!entry) return;
    setSelected((cur) => {
      if (cur.some((s) => s.heroInclusionId === id)) return cur;
      const next = [
        ...cur,
        {
          heroInclusionId: entry.id,
          name: entry.name,
          iconImageId: entry.iconImageId,
        },
      ];
      if (isControlled) onDraftChange(next.map((s) => s.heroInclusionId));
      return next;
    });
  };
  // Create a brand-new hero-inclusion master entry inline, then select it.
  const createHero = async (name: string): Promise<void> => {
    try {
      const h = await createMaster.mutateAsync({ name });
      setSelected((cur) => {
        if (cur.some((s) => s.heroInclusionId === h.id)) return cur;
        const next = [...cur, { heroInclusionId: h.id, name: h.name, iconImageId: h.iconImageId }];
        if (isControlled) onDraftChange(next.map((s) => s.heroInclusionId));
        return next;
      });
    } catch {
      /* error surfaced by the create hook's toast */
    }
  };

  const remove = (id: string): void =>
    setSelected((cur) => {
      const next = cur.filter((s) => s.heroInclusionId !== id);
      if (isControlled) onDraftChange(next.map((s) => s.heroInclusionId));
      return next;
    });
  const move = (index: number, dir: -1 | 1): void =>
    setSelected((cur) => {
      const next = index + dir;
      if (next < 0 || next >= cur.length) return cur;
      const copy = [...cur];
      const a = copy[index];
      const b = copy[next];
      if (!a || !b) return cur;
      copy[index] = b;
      copy[next] = a;
      if (isControlled) onDraftChange(copy.map((s) => s.heroInclusionId));
      return copy;
    });

  const save = (): void => {
    void set.mutateAsync({
      heroInclusionIds: selected.map((s) => s.heroInclusionId),
    });
  };

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-zinc-900">Hero Highlights</h3>
        <p className="text-xs text-zinc-500">
          Icon + label strip shown in the website hero. Manage the entries themselves under Masters
          → Hero Inclusions.
        </p>
      </div>

      {masterQuery.isError && <UzAlert tone="destructive">Failed to load hero inclusions.</UzAlert>}
      {!masterQuery.isLoading && master.length === 0 && !masterQuery.isError && (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          No hero inclusion entries exist yet. Create them under Masters → Hero Inclusions.
        </p>
      )}

      <UzCombobox
        placeholder="Add a hero highlight…"
        searchPlaceholder="Search or type to create…"
        value=""
        onValueChange={add}
        options={options}
        onCreate={(name) => void createHero(name)}
        createLabel={(q) => `Create “${q}”`}
        existingLabels={master.map((m) => m.name)}
        loading={masterQuery.isLoading}
        disabled={
          (isControlled ? false : set.isPending) || masterQuery.isLoading || createMaster.isPending
        }
        emptyText="No matching hero inclusions"
      />

      {selected.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          None selected yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {selected.map((s, index) => (
            <li
              key={s.heroInclusionId}
              className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2 text-sm"
            >
              <IconThumb id={s.iconImageId} />
              <span className="truncate">{s.name}</span>
              <div className="ml-auto flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={(isControlled ? false : set.isPending) || index === 0}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-40"
                  aria-label="Move up"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={(isControlled ? false : set.isPending) || index === selected.length - 1}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-40"
                  aria-label="Move down"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(s.heroInclusionId)}
                  disabled={isControlled ? false : set.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!isControlled && (
        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-xs text-zinc-400">
            {dirty ? 'Unsaved changes' : 'Saved'}
          </span>
          <UzButton size="sm" onClick={save} loading={set.isPending} disabled={!dirty}>
            Save highlights
          </UzButton>
        </div>
      )}
    </section>
  );
};
